import   React         from 'react';

import   classNames    from 'classnames';

import { ShieldCheck ,
         ShieldAlert } from 'lucide-react';

import   styles        from './ChatModeration.module.scss';

import { ChatModChannel         ,
         ChatModMessage         ,
         CHAT_MODERATION_LEVELS } from '../../../dataTypes/moderationManagment/chat_types';

import { pct           ,
         shortDuration ,
         secondsSince  ,
         RISK          }  from './chatModerationUi';

/**
 * Le spectre de risque : chaque message des dix dernières minutes est un point
 * posé sur l'échelle de 0 à 100 de l'IA, une ligne par chaîne suivie, sur le
 * fond des trois zones de SA chaîne (publié / à vous / refusé). On voit d'un
 * coup d'œil ce que l'IA fait seule, et ce qu'elle vous laisse ; un point
 * ambre (à valider) s'ouvre d'un clic dans la file.
 */

const WINDOW_MS = 10 * 60 * 1000;

interface Props {
  channels   : ChatModChannel[];
  pending    : ChatModMessage[];
  recent     : ChatModMessage[];
  colorOf    : (id: number) => string;
  now        : number;
  focusedId  : number | null;
  freshIds   : Set<number>;
  onFocus    : (id: number) => void;
}

const RiskSpectrum: React.FC<Props> = ({ channels, pending, recent, colorOf, now, focusedId, freshIds, onFocus }) => {

  const inWindow = (m: ChatModMessage) => m.creationDate !== null && now - Date.parse(m.creationDate) < WINDOW_MS;
  const points   = [...recent.filter(inWindow), ...pending];

  const hourTotal    = channels.reduce((n, c) => n + c.totalHour, 0);
  const hourByAi     = channels.reduce((n, c) => n + c.byAiHour, 0);
  const hourRejected = channels.reduce((n, c) => n + c.rejectedHour, 0);
  const oldest       = pending.length > 0 ? secondsSince(pending[0].creationDate, now) : 0;

  // L'IA est en panne si les derniers messages n'ont pas de score.
  const latest   = [...pending, ...recent].sort((a, b) => b.id - a.id).slice(0, 6);
  const aiDown   = latest.length >= 3 && latest.every(m => m.aiEngine === 'aucun' || m.aiScore === null);
  const engine   = latest.find(m => m.aiEngine && m.aiEngine !== 'aucun')?.aiEngine;

  return (
    <section className={classNames(styles.panel, styles.spectrum)} aria-label="Spectre de risque">

      <header className={styles.panelHead}>
        <div className={styles.kpis}>
          <span className={classNames(oldest > 60 && styles.kpiAlert)}>
            <b>{pending.length}</b> <span className={styles.muted}>à valider</span>
            {pending.length > 0 && <span className={classNames(oldest > 60 ? styles.kpiAlert : styles.muted)}> · le plus ancien {shortDuration(oldest)}</span>}
          </span>
          <span>
            <b>{hourTotal}</b> <span className={styles.muted}>messages cette heure</span>
          </span>
          <span>
            <b>{hourTotal > 0 ? Math.round(hourByAi * 100 / hourTotal) : 0} %</b> <span className={styles.muted}>tranchés par l'IA</span>
          </span>
          <span>
            <b>{hourRejected}</b> <span className={styles.muted}>refusés</span>
          </span>
        </div>
        <span className={styles.spacer} />
        {aiDown
          ? <span className={classNames(styles.aiState, styles.aiDown)} title="Les derniers messages n'ont pas été notés : ils partent tous à valider.">
              <ShieldAlert size={15} /> IA indisponible, tout passe par vous
            </span>
          : <span className={classNames(styles.aiState, styles.aiOk)}>
              <ShieldCheck size={15} /> IA {engine === 'claude' ? 'Claude (repli)' : 'Google'} active
            </span>}
      </header>

      <div className={styles.lanes}>
        {channels.map(channel => {
          const level  = CHAT_MODERATION_LEVELS[channel.level] ?? CHAT_MODERATION_LEVELS[1];
          const manual = level.publish === null || level.reject === null;
          const pubPct = manual ? 0   : level.publish! * 100;
          const rejPct = manual ? 100 : level.reject!  * 100;
          const mine   = points.filter(m => m.channelId === channel.id);

          return (
            <div key={channel.id} className={styles.lane} style={{ ['--chan' as any]: colorOf(channel.id) }}>
              <span className={styles.laneLabel} title={`${channel.name} — modération ${level.label.toLowerCase()}`}>
                <span className={styles.swatch} /> {channel.name}
              </span>
              <div className={styles.track}>
                {!manual && <span className={classNames(styles.zone, styles.zoneOk)}  style={{ left: 0, width: `${pubPct}%` }} />}
                <span className={classNames(styles.zone, styles.zoneDoubt)} style={{ left: `${pubPct}%`, width: `${rejPct - pubPct}%`,
                        borderRadius: manual ? 5 : undefined }} />
                {!manual && <span className={classNames(styles.zone, styles.zoneBad)} style={{ left: `${rejPct}%`, right: 0 }} />}

                {mine.map(m => {
                  // Sans score (IA en panne) : au milieu de la zone « à vous ».
                  const x = m.aiScore === null ? (pubPct + rejPct) / 2 : Math.min(99.4, Math.max(0.6, m.aiScore * 100));
                  const label = `${m.pseudo} · ${pct(m.aiScore)} — ${m.text.slice(0, 80)}`;

                  if (m.status === 'pending') {
                    return (
                      <button key={m.id} type="button" title={label} aria-label={`À valider : ${label}`}
                              className={classNames(styles.dot, styles.dotPending, focusedId === m.id && styles.dotFocused, freshIds.has(m.id) && styles.dotFresh)}
                              style={{ left: `${x}%` }}
                              onClick={() => onFocus(m.id)} />
                    );
                  }
                  return (
                    <span key={m.id} title={label}
                          className={classNames(styles.dot,
                                                m.status === 'approved' ? styles.dotApproved : styles.dotRejected,
                                                m.moderatedBy === 'admin' && styles.dotAdmin,
                                                freshIds.has(m.id) && styles.dotFresh)}
                          style={{ left: `${x}%` }} />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.axis}>
        <div className={styles.legend}>
          <span><i style={{ background: RISK.ok }} />publié</span>
          <span><i style={{ background: RISK.doubt }} />à vous</span>
          <span><i style={{ background: RISK.bad }} />refusé</span>
        </div>
        <div className={styles.axisScale} aria-hidden>
          {[0, 25, 50, 75, 100].map(v => <span key={v} style={{ left: `${v}%` }}>{v}</span>)}
        </div>
      </div>
    </section>
  );
};

export default RiskSpectrum;
